import React, { useEffect, useMemo, useState } from 'react';
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { publicApi } from '../../api/http';
import PageLoader from '../../components/PageLoader/PageLoader';
import { DEFAULT_PLAYER_IMAGE } from '../../components/DraftOdds/DraftOddsComponents';
import {
    getProspectPreviewTeam,
    getProspectPreviewTeamIndex,
    isTeamPreviewAvailable,
    formatReleaseLabel,
    getTeamReleaseDate,
    nhlTeamLogoUrl,
} from './teams';
import './ProspectPreviewTeam.css';

const COLUMNS = {
    rank: 'rank',
    player: 'player',
    position: 'position',
    draft_year: 'draft_year',
    overall_pick: 'overall_pick',
    current: 'current',
    highest_league: 'highest_league',
    nhl_games_played: 'nhl_games_played',
    p_nhl_next_year: 'p_nhl_next_year',
    p_nhler: 'p_nhler',
    p_impact: 'p_impact',
    p_bust: 'p_bust',
    blend_rank: 'blend_rank',
    board_score: 'board_score',
    nhle_ppg: 'nhle_ppg',
    proj_peak_gar: 'proj_peak_gar',
    proj_gar_floor: 'proj_gar_floor',
    proj_gar_ceiling: 'proj_gar_ceiling',
    arrival_year: 'arrival_year',
};

const COLUMN_META = {
    [COLUMNS.rank]: {
        label: '#',
        tooltip:
            'Default model ranking for this team\'s prospect pool (1 = highest). Based primarily on blend rank, then board score and projected peak GAR.',
    },
    [COLUMNS.player]: {
        label: 'Player',
        tooltip: 'Prospect name. Linked to their full Hockey Pipeline player profile.',
    },
    [COLUMNS.position]: {
        label: 'Pos',
        tooltip: 'Primary playing position from the prospect model (or player profile if missing).',
    },
    [COLUMNS.draft_year]: {
        label: 'Draft',
        tooltip: 'NHL Entry Draft year the player was selected in (or is eligible for).',
    },
    [COLUMNS.overall_pick]: {
        label: 'Overall',
        tooltip:
            'Overall NHL Entry Draft pick number (round and pick converted to a single slot). Blank if the player was undrafted.',
    },
    [COLUMNS.current]: {
        label: 'Currently',
        tooltip:
            'Most recent team and league the player appeared for in synced season totals, including the season label when available.',
    },
    [COLUMNS.highest_league]: {
        label: 'Highest',
        tooltip:
            'Highest club competition level in the player\'s synced season history (NHL, AHL, European pro, NCAA, CHL, etc.). International tournaments like WJC are excluded.',
    },
    [COLUMNS.nhl_games_played]: {
        label: 'NHL GP',
        tooltip:
            'Career regular-season NHL games played. 0 means the player has not appeared in an NHL regular-season game yet.',
    },
    [COLUMNS.p_nhl_next_year]: {
        label: 'NHL next',
        tooltip:
            'Model probability the player plays in the NHL next season (dy0 / year-0 NHL projection).',
    },
    [COLUMNS.p_nhler]: {
        label: 'P(NHL)',
        tooltip:
            'Model probability the player becomes an NHL player at some point in their career.',
    },
    [COLUMNS.p_impact]: {
        label: 'P(Impact)',
        tooltip:
            'Model probability the player becomes an impact NHL contributor rather than a fringe or depth player.',
    },
    [COLUMNS.p_bust]: {
        label: 'P(Bust)',
        tooltip:
            'Model probability the player does not pan out relative to draft/prospect expectations.',
    },
    [COLUMNS.blend_rank]: {
        label: 'Blend',
        tooltip:
            'Blended model ranking across projection signals. Lower numbers are better.',
    },
    [COLUMNS.board_score]: {
        label: 'Board',
        tooltip:
            'Overall board score from the prospect model. Higher scores indicate a stronger prospect.',
    },
    [COLUMNS.nhle_ppg]: {
        label: 'NHLe PPG',
        tooltip:
            'NHL-equivalent points per game after translating production from the player\'s league using NHLe factors.',
    },
    [COLUMNS.proj_peak_gar]: {
        label: 'Peak GAR',
        tooltip:
            'Projected peak Goals Above Replacement value across the player\'s NHL career outlook.',
    },
    [COLUMNS.proj_gar_floor]: {
        label: 'GAR floor',
        tooltip:
            'Conservative / downside projected Goals Above Replacement outcome from the model.',
    },
    [COLUMNS.proj_gar_ceiling]: {
        label: 'GAR ceil',
        tooltip:
            'Optimistic / upside projected Goals Above Replacement outcome from the model.',
    },
    [COLUMNS.arrival_year]: {
        label: 'Arrival',
        tooltip:
            'Model-estimated calendar year the player is projected to arrive as an NHL regular contributor.',
    },
};

const DEFAULT_DESC = new Set([
    COLUMNS.draft_year,
    COLUMNS.nhl_games_played,
    COLUMNS.p_nhl_next_year,
    COLUMNS.p_nhler,
    COLUMNS.p_impact,
    COLUMNS.board_score,
    COLUMNS.nhle_ppg,
    COLUMNS.proj_peak_gar,
    COLUMNS.proj_gar_floor,
    COLUMNS.proj_gar_ceiling,
]);

function formatPct(value) {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${(Number(value) * 100).toFixed(1)}%`;
}

function formatNum(value, digits = 2) {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return Number(value).toFixed(digits);
}

function playerSortKey(row) {
    return `${row.last_name || ''} ${row.first_name || ''}`.trim().toLowerCase();
}

function currentSortKey(row) {
    return `${row.current_league || ''} ${row.current_team || ''}`.trim().toLowerCase();
}

function compareNullableNumber(a, b, ascending) {
    const aNull = a == null || Number.isNaN(Number(a));
    const bNull = b == null || Number.isNaN(Number(b));
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    const diff = Number(a) - Number(b);
    return ascending ? diff : -diff;
}

function compareRows(a, b, orderBy, order) {
    const ascending = order === 'asc';

    switch (orderBy) {
        case COLUMNS.player: {
            const cmp = playerSortKey(a).localeCompare(playerSortKey(b));
            return ascending ? cmp : -cmp;
        }
        case COLUMNS.position: {
            const cmp = (a.position || '').localeCompare(b.position || '');
            return ascending ? cmp : -cmp;
        }
        case COLUMNS.current: {
            const cmp = currentSortKey(a).localeCompare(currentSortKey(b));
            return ascending ? cmp : -cmp;
        }
        case COLUMNS.highest_league: {
            const cmp = (a.highest_league || '').localeCompare(b.highest_league || '');
            return ascending ? cmp : -cmp;
        }
        case COLUMNS.rank:
            return compareNullableNumber(a.rank, b.rank, ascending);
        case COLUMNS.draft_year:
            return compareNullableNumber(a.draft_year, b.draft_year, ascending);
        case COLUMNS.overall_pick:
            return compareNullableNumber(a.overall_pick, b.overall_pick, ascending);
        case COLUMNS.nhl_games_played:
            return compareNullableNumber(a.nhl_games_played, b.nhl_games_played, ascending);
        case COLUMNS.p_nhl_next_year:
            return compareNullableNumber(a.p_nhl_next_year, b.p_nhl_next_year, ascending);
        case COLUMNS.p_nhler:
            return compareNullableNumber(a.p_nhler, b.p_nhler, ascending);
        case COLUMNS.p_impact:
            return compareNullableNumber(a.p_impact, b.p_impact, ascending);
        case COLUMNS.p_bust:
            return compareNullableNumber(a.p_bust, b.p_bust, ascending);
        case COLUMNS.blend_rank:
            return compareNullableNumber(a.blend_rank, b.blend_rank, ascending);
        case COLUMNS.board_score:
            return compareNullableNumber(a.board_score, b.board_score, ascending);
        case COLUMNS.nhle_ppg:
            return compareNullableNumber(a.nhle_ppg, b.nhle_ppg, ascending);
        case COLUMNS.proj_peak_gar:
            return compareNullableNumber(a.proj_peak_gar, b.proj_peak_gar, ascending);
        case COLUMNS.proj_gar_floor:
            return compareNullableNumber(a.proj_gar_floor, b.proj_gar_floor, ascending);
        case COLUMNS.proj_gar_ceiling:
            return compareNullableNumber(a.proj_gar_ceiling, b.proj_gar_ceiling, ascending);
        case COLUMNS.arrival_year:
            return compareNullableNumber(a.arrival_year, b.arrival_year, ascending);
        default:
            return 0;
    }
}

function SortHeader({ column, orderBy, order, onSort, align = 'left', className = '' }) {
    const meta = COLUMN_META[column];

    return (
        <TableCell align={align} className={className}>
            <div
                className={`prospect-preview-team-header ${
                    align === 'right' ? 'prospect-preview-team-header--right' : ''
                }`}
            >
                <TableSortLabel
                    active={orderBy === column}
                    direction={orderBy === column ? order : 'asc'}
                    onClick={() => onSort(column)}
                    className="prospect-preview-team-sort-label"
                >
                    {meta.label}
                </TableSortLabel>
                <Tooltip
                    title={meta.tooltip}
                    arrow
                    enterTouchDelay={0}
                    describeChild
                    componentsProps={{
                        tooltip: { className: 'prospect-preview-team-tooltip' },
                    }}
                >
                    <span
                        className="prospect-preview-team-info"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        role="img"
                        aria-label={`${meta.label} info`}
                    >
                        <InfoOutlinedIcon fontSize="inherit" />
                    </span>
                </Tooltip>
            </div>
        </TableCell>
    );
}

function ProspectPreviewTeam() {
    const { team_id } = useParams();
    const teamMeta = getProspectPreviewTeam(team_id);
    const teamIndex = getProspectPreviewTeamIndex(team_id);
    const available = teamIndex >= 0 && isTeamPreviewAvailable(teamIndex);

    const [loading, setLoading] = useState(available);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [orderBy, setOrderBy] = useState(COLUMNS.rank);
    const [order, setOrder] = useState('asc');

    useEffect(() => {
        if (!available || !teamMeta) {
            setLoading(false);
            setPayload(null);
            setError(null);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        publicApi
            .get(`/prospect_preview/${teamMeta.id}`, {
                params: { abbreviation: teamMeta.abbr },
            })
            .then((res) => {
                if (!cancelled) {
                    setPayload(res.data);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setPayload(null);
                    setError(err);
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [available, teamMeta]);

    const sortedProspects = useMemo(() => {
        const rows = payload?.prospects || [];
        return [...rows].sort((a, b) => {
            const cmp = compareRows(a, b, orderBy, order);
            if (cmp !== 0) return cmp;
            return (a.rank ?? 0) - (b.rank ?? 0);
        });
    }, [payload, orderBy, order]);

    const handleRequestSort = (column) => {
        if (orderBy === column) {
            setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setOrderBy(column);
        setOrder(DEFAULT_DESC.has(column) ? 'desc' : 'asc');
    };

    if (!teamMeta) {
        return (
            <Container className="container top-container">
                <Typography className="page-title">Team not found</Typography>
                <Link to="/prospect-preview" className="prospect-preview-team-back">
                    Back to Prospect Preview
                </Link>
            </Container>
        );
    }

    const logoUrl = nhlTeamLogoUrl(teamMeta.abbr);
    const releaseLabel = formatReleaseLabel(getTeamReleaseDate(teamIndex));

    return (
        <>
            <Helmet>
                <title>{`${teamMeta.name} Prospect Preview - Hockey Pipeline`}</title>
                <meta
                    name="description"
                    content={`Model-ranked prospect pool for the ${teamMeta.name}, based on current NHL rights ownership.`}
                />
                <link
                    rel="canonical"
                    href={`https://hockeypipeline.com/prospect-preview/${teamMeta.id}`}
                />
            </Helmet>

            <PageLoader loading={loading} />

            <Container className="container top-container prospect-preview-team-page">
                <Link to="/prospect-preview" className="prospect-preview-team-back">
                    <ArrowBackIcon fontSize="small" />
                    All teams
                </Link>

                <div
                    className="prospect-preview-team-hero"
                    style={{
                        background: `linear-gradient(125deg, ${teamMeta.color} 0%, color-mix(in srgb, ${teamMeta.color} 55%, #000) 55%, #0b0d10 100%)`,
                    }}
                >
                    <img
                        src={logoUrl}
                        alt=""
                        aria-hidden="true"
                        className="prospect-preview-team-hero-logo"
                    />
                    <div className="prospect-preview-team-hero-copy">
                        <Typography className="prospect-preview-team-eyebrow">
                            2026-2027 Prospect Preview
                        </Typography>
                        <Typography variant="h4" component="h1" className="prospect-preview-team-title">
                            {teamMeta.name}
                        </Typography>
                        <Typography className="prospect-preview-team-subtitle">
                            Prospects ranked by model projections for players currently under this
                            team&apos;s NHL rights. Sort any column to explore the pool.
                        </Typography>
                    </div>
                </div>

                {!available ? (
                    <div className="prospect-preview-team-locked">
                        <LockOutlinedIcon className="prospect-preview-team-locked-icon" />
                        <Typography className="prospect-preview-team-locked-text">
                            {`Coming on ${releaseLabel}`}
                        </Typography>
                    </div>
                ) : error ? (
                    <Typography className="prospect-preview-team-empty">
                        Could not load prospect rankings for this team.
                    </Typography>
                ) : !loading && payload ? (
                    <>
                        <Typography className="prospect-preview-team-count">
                            {`${payload.total} prospect${payload.total === 1 ? '' : 's'}`}
                        </Typography>

                        {payload.prospects.length === 0 ? (
                            <Typography className="prospect-preview-team-empty">
                                No model-ranked prospects found for this team yet.
                            </Typography>
                        ) : (
                            <TableContainer className="prospect-preview-team-table-wrap">
                                <Table size="small" className="prospect-preview-team-table">
                                    <TableHead>
                                        <TableRow>
                                            <SortHeader
                                                column={COLUMNS.rank}
                                                orderBy={orderBy}
                                                order={order}
                                                onSort={handleRequestSort}
                                                className="prospect-preview-team-sticky prospect-preview-team-sticky--rank"
                                            />
                                            <SortHeader
                                                column={COLUMNS.player}
                                                orderBy={orderBy}
                                                order={order}
                                                onSort={handleRequestSort}
                                                className="prospect-preview-team-sticky prospect-preview-team-sticky--player"
                                            />
                                            <SortHeader column={COLUMNS.position} orderBy={orderBy} order={order} onSort={handleRequestSort} />
                                            <SortHeader column={COLUMNS.draft_year} orderBy={orderBy} order={order} onSort={handleRequestSort} />
                                            <SortHeader column={COLUMNS.overall_pick} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.current} orderBy={orderBy} order={order} onSort={handleRequestSort} />
                                            <SortHeader column={COLUMNS.highest_league} orderBy={orderBy} order={order} onSort={handleRequestSort} />
                                            <SortHeader column={COLUMNS.nhl_games_played} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.p_nhl_next_year} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.p_nhler} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.p_impact} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.p_bust} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.blend_rank} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.board_score} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.nhle_ppg} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.proj_peak_gar} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.proj_gar_floor} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.proj_gar_ceiling} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                            <SortHeader column={COLUMNS.arrival_year} orderBy={orderBy} order={order} onSort={handleRequestSort} align="right" />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedProspects.map((row) => (
                                            <TableRow
                                                key={row.player_id}
                                                hover
                                                className="prospect-preview-team-row"
                                            >
                                                <TableCell className="prospect-preview-team-rank prospect-preview-team-sticky prospect-preview-team-sticky--rank">
                                                    {row.rank}
                                                </TableCell>
                                                <TableCell className="prospect-preview-team-sticky prospect-preview-team-sticky--player">
                                                    <Link
                                                        to={`/player/${row.player_id}`}
                                                        className="prospect-preview-team-player"
                                                    >
                                                        <img
                                                            src={row.player_image_url || DEFAULT_PLAYER_IMAGE}
                                                            alt=""
                                                            className="prospect-preview-team-player-img"
                                                        />
                                                        <span className="prospect-preview-team-player-name">
                                                            {row.first_name} {row.last_name}
                                                        </span>
                                                    </Link>
                                                </TableCell>
                                                <TableCell>{row.position || '—'}</TableCell>
                                                <TableCell>{row.draft_year || '—'}</TableCell>
                                                <TableCell align="right">
                                                    {row.overall_pick ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {row.current_team || row.current_league ? (
                                                        <div className="prospect-preview-team-playing">
                                                            <span className="prospect-preview-team-playing-team">
                                                                {row.current_team || '—'}
                                                            </span>
                                                            <span className="prospect-preview-team-playing-league">
                                                                {row.current_league || '—'}
                                                                {row.current_season ? ` · ${row.current_season}` : ''}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell>{row.highest_league || '—'}</TableCell>
                                                <TableCell align="right">
                                                    {row.has_nhl_games ? row.nhl_games_played : '0'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatPct(row.p_nhl_next_year)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatPct(row.p_nhler)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatPct(row.p_impact)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatPct(row.p_bust)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.blend_rank ?? '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNum(row.board_score)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNum(row.nhle_ppg)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNum(row.proj_peak_gar)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNum(row.proj_gar_floor)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNum(row.proj_gar_ceiling)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.arrival_year ?? '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                ) : null}
            </Container>
        </>
    );
}

export default ProspectPreviewTeam;
